import React from 'react'
import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'
import type { RangeKey } from '@dataTypes/dashboard.types'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import type { ScheduleOperationsOverviewResult } from '@services/api/dashboard/operations-schedule.api'
import type { SchedulingPerformanceOverviewResult } from '@services/api/dashboard/scheduling-performance.api'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  mapScheduleOperationsCapacityMetrics,
  mapScheduleOperationsDisruptionsMetrics,
} from '@utils/scheduleOperationsOverview.util'
import {
  mapSchedulingPerformanceOperatoryMetrics,
  mapSchedulingPerformanceOverviewMetrics,
} from '@utils/schedulingPerformanceOverview.util'
import {
  DashboardMetricTileGrid,
  pickMetricsByIds,
} from '../Shared/DashboardMetricTileGrid.component'
import {
  getOperationsScheduleOverviewColumnMetrics,
  getOperationsOperatoryEfficiencyColumnMetrics,
  getOperationsScheduleDisruptionsMetrics,
  getOperationsCapacityOpeningsMetrics,
} from '../Operations/operationsMockData'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import './DashboardSchedulingPerformancePanel.style.css'

const SCHEDULE_OVERVIEW_IDS = [
  'scheduled-appointments',
  'scheduled-patients',
  'scheduled-production',
  'broken-appointments',
  'no-show',
  'reschedule-rate',
]

const OPERATORY_EFFICIENCY_IDS = [
  'schedule-utilization',
  'open-hours',
  'total-worked-days',
  'missing-procedure-codes',
  'unassn-procedures',
  'schedules-wo-code',
]

const SCHEDULE_DISRUPTIONS_IDS = [
  'ops-broken-appointments',
  'ops-no-show',
  'ops-reschedule-rate',
  'ops-same-day-cancellations',
  'ops-appointment-overruns',
  'ops-lost-chair-time',
]

const CAPACITY_OPENINGS_IDS = [
  'ops-open-chair-time',
  'ops-unfilled-slots',
  'ops-wait-list',
  'ops-confirmations-pending',
  'ops-new-patient-openings',
  'ops-hygiene-openings',
]

type DashboardSchedulingPerformancePanelProps = {
  firstColumnLabel: string
  range: RangeKey
  /** Performance tab: schedule + operatory. Operations tab: disruptions + capacity. */
  variant?: 'scheduling' | 'operations'
  secondColumnLabel?: string
  loading: boolean
  scheduleOpsOverview?: ScheduleOperationsOverviewResult | null
  schedulingPerformanceOverview?: SchedulingPerformanceOverviewResult | null
  /** When set, wrap the operations panel in a section card. */
  title?: string
  titleHref?: string
  className?: string
}

function SchedulingPanelSection({
  label,
  metrics,
}: {
  label: string
  metrics: DashboardMetricItemData[]
}): React.ReactElement {
  return (
    <div className="dashboard-scheduling-panel-section">
      <div className="dashboard-scheduling-panel-section-label">{label}</div>
      <DashboardMetricTileGrid metrics={metrics} columns={3} />
    </div>
  )
}

function OperationsScheduleMetrics({
  range,
  firstColumnLabel,
  secondColumnLabel,
  title,
  titleHref,
  className,
  loading,
  scheduleOpsOverview,
}: {
  range: RangeKey
  firstColumnLabel: string
  secondColumnLabel: string
  title?: string
  titleHref?: string
  className?: string
  loading: boolean
  scheduleOpsOverview: ScheduleOperationsOverviewResult | null
}): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const scheduleOpsKpis = scheduleOpsOverview?.kpis
  // Capacity-only fields (openChairTime / unfilledSlots) are excluded: they can
  // be nonzero on an inactive schedule and must not keep the card populated.
  const hasSectionData = hasNonZeroDashboardValues(
    scheduleOpsKpis?.brokenAppointments,
    scheduleOpsKpis?.noShow,
    scheduleOpsKpis?.rescheduleRate,
    scheduleOpsKpis?.sameDayCancellations,
    scheduleOpsKpis?.appointmentOverruns,
    scheduleOpsKpis?.lostChairTime,
    scheduleOpsKpis?.waitListPatients,
    scheduleOpsKpis?.confirmationsPending,
    scheduleOpsKpis?.newPatientOpenings,
    scheduleOpsKpis?.hygieneOpenings,
  )
  const hasPayload = scheduleOpsOverview != null
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasSectionData)
  const pending = isDashboardSectionPending(loading, hasPayload)
  const liveKpis = scheduleOpsOverview?.kpis
  const topMetrics = pickMetricsByIds(
    liveKpis
      ? mapScheduleOperationsDisruptionsMetrics(liveKpis)
      : withDashboardMetricShellValues(
          getOperationsScheduleDisruptionsMetrics(range),
          useMocks,
        ),
    SCHEDULE_DISRUPTIONS_IDS,
  )
  const bottomMetrics = pickMetricsByIds(
    liveKpis
      ? mapScheduleOperationsCapacityMetrics(liveKpis)
      : withDashboardMetricShellValues(
          getOperationsCapacityOpeningsMetrics(range),
          useMocks,
        ),
    CAPACITY_OPENINGS_IDS,
  )
  const content = (
    <div className="dashboard-scheduling-panel-sections">
      <SchedulingPanelSection label={firstColumnLabel} metrics={topMetrics} />
      <SchedulingPanelSection
        label={secondColumnLabel}
        metrics={bottomMetrics}
      />
    </div>
  )

  if (!titleHref) return content

  return (
    <DashboardSectionCard
      title={title}
      titleHref={titleHref}
      className={className}
      loading={pending}
      skeleton={{ metrics: 6, metricColumns: 3, block: 'tall' }}
      isEmpty={isEmpty}
    >
      {content}
    </DashboardSectionCard>
  )
}

function PerformanceScheduleMetrics({
  range,
  firstColumnLabel,
  secondColumnLabel,
  title,
  titleHref,
  className,
  loading,
  schedulingPerformanceOverview,
}: {
  range: RangeKey
  firstColumnLabel: string
  secondColumnLabel: string
  title?: string
  titleHref?: string
  className?: string
  loading: boolean
  schedulingPerformanceOverview: SchedulingPerformanceOverviewResult | null
}): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const schedulingKpis = schedulingPerformanceOverview?.kpis
  // Capacity-only fields (openHours / totalWorkedDays) are excluded: they can
  // be nonzero on an inactive schedule and must not keep the card populated.
  const hasSectionData = hasNonZeroDashboardValues(
    schedulingKpis?.scheduledAppointments,
    schedulingKpis?.scheduledPatients,
    schedulingKpis?.scheduledProduction,
    schedulingKpis?.brokenAppointments,
    schedulingKpis?.noShow,
    schedulingKpis?.rescheduleRate,
    schedulingKpis?.scheduleUtilization,
    schedulingKpis?.missingProcedureCodes,
    schedulingKpis?.unassnProcedures,
    schedulingKpis?.schedulesWoCode,
  )
  const hasPayload = schedulingPerformanceOverview != null
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasSectionData)
  const pending = isDashboardSectionPending(loading, hasPayload)
  const liveKpis = schedulingPerformanceOverview?.kpis
  const topMetrics = pickMetricsByIds(
    liveKpis
      ? mapSchedulingPerformanceOverviewMetrics(liveKpis)
      : withDashboardMetricShellValues(
          getOperationsScheduleOverviewColumnMetrics(range).filter(
            (m) => m.id !== 'claims-outstanding',
          ),
          useMocks,
        ),
    SCHEDULE_OVERVIEW_IDS,
  )
  const bottomMetrics = pickMetricsByIds(
    liveKpis
      ? mapSchedulingPerformanceOperatoryMetrics(liveKpis)
      : withDashboardMetricShellValues(
          getOperationsOperatoryEfficiencyColumnMetrics(range),
          useMocks,
        ),
    OPERATORY_EFFICIENCY_IDS,
  )
  const content = (
    <div className="dashboard-scheduling-panel-sections">
      <SchedulingPanelSection label={firstColumnLabel} metrics={topMetrics} />
      <SchedulingPanelSection
        label={secondColumnLabel}
        metrics={bottomMetrics}
      />
    </div>
  )

  if (!titleHref) return content

  return (
    <DashboardSectionCard
      title={title}
      titleHref={titleHref}
      className={className}
      loading={pending}
      skeleton={{ metrics: 6, metricColumns: 3, block: 'tall' }}
      isEmpty={isEmpty}
    >
      {content}
    </DashboardSectionCard>
  )
}

/**
 * Two stacked KPI subsections (3×2 tiles). Variant selects data source:
 * - `scheduling` — Performance tab (schedule overview + operatory efficiency)
 * - `operations` — Operations tab (disruptions + capacity / openings)
 */
function DashboardSchedulingPerformancePanel({
  firstColumnLabel,
  range,
  variant = 'scheduling',
  secondColumnLabel,
  title,
  titleHref,
  className,
  loading,
  scheduleOpsOverview = null,
  schedulingPerformanceOverview = null,
}: DashboardSchedulingPerformancePanelProps): React.ReactElement {
  const isOperations = variant === 'operations'
  const bottomLabel =
    secondColumnLabel ??
    (isOperations ? 'Capacity & openings' : 'Operatory efficiency')

  if (isOperations) {
    return (
      <OperationsScheduleMetrics
        range={range}
        firstColumnLabel={firstColumnLabel}
        secondColumnLabel={bottomLabel}
        title={title}
        titleHref={titleHref}
        className={className}
        loading={loading}
        scheduleOpsOverview={scheduleOpsOverview}
      />
    )
  }

  return (
    <PerformanceScheduleMetrics
      range={range}
      firstColumnLabel={firstColumnLabel}
      secondColumnLabel={bottomLabel}
      title={title}
      titleHref={titleHref}
      className={className}
      loading={loading}
      schedulingPerformanceOverview={schedulingPerformanceOverview}
    />
  )
}

export default DashboardSchedulingPerformancePanel
